import { type ColorValue, Platform, type StyleSheet, type TextProps } from 'react-native'
import { fontFamilies } from '@/src/common/theme/fonts'

const mesaPrimary = '#00DFC9'

const AcreTurquoise800 = '#086762'

export const mesaColorNames = {
  'Mesa Primary Scale/500 #00C7B6': '#00C7B6',
  'Mesa Primary Scale/400 - Brand #00DFC9': mesaPrimary,
  'Mesa - Brand/Secondary #223843': '#223843',
  'Mesa - Brand/White #FFFFFF': '#FFFFFF',
  'Mesa - Brand/Blackish #222222': '#222222',
  'Acre Turquoise/500 #00DFC9': mesaPrimary,
  'Acre Turquoise/700 #02837A': '#02837A',
  'Acre Turquoise/800 #086762': AcreTurquoise800,
  'Greyscale/900 #152438': '#152438',
  'Greyscale/600 #505D64': '#505D64',
  'Greyscale/500 #6E7375': '#6E7375',
  'Greyscale/400 #A4AAAC': '#A4AAAC',
  'Greyscale/300 #CDD4D5': '#CDD4D5',
  'Greyscale/200 #EBF0F1': '#EBF0F1',
  'Greyscale/100 #F5F8F8': '#F5F8F8',
  'Greyscale/0 #FFFFFF': '#FFFFFF',
  'Black/5% rgba(28, 28, 28, 0.05)': 'rgba(28, 28, 28, 0.05)',
  'Black/10% rgba(28, 28, 28, 0.1)': 'rgba(28, 28, 28, 0.1)',
  'Wide/100 #C6FFF5': '#C6FFF5',
  'Wide/600 #00A498': '#00A498',
  'Mesa Third #E78A43': '#E78A43',
  'Primary/Brand #1C1C1C': '#1C1C1C',
  'Primary/Light #F7F9FB': '#F7F9FB',
  'semantic/fg/base #131214': '#131214',
  'semantic/fg/subtle #898D8F': '#898D8F',
  'semantic/fg/success #008557': '#008557',
  'semantic/fg/error #DB340B': '#DB340B',
  'semantic/fg/disabled #898D8F': '#898D8F',
  'semantic/bg/disabled #DADDDE': '#DADDDE',
  'semantic/bg/success rgba(232, 250, 240, 1)': 'rgba(232, 250, 240, 1)',
  'global/grey/80 #2F3133': '#2F3133',
  'global/grey/70 #53575A': '#53575A',
  'global/grey/60 #6E7375': '#6E7375',
  'global/grey/20 #E6E9EB': '#E6E9EB',
  'legacy/rneui/grey0': '#393e42',
} as const satisfies Record<string, ColorValue>

const mesaColorExtensions = {
  primary500: mesaColorNames['Mesa Primary Scale/500 #00C7B6'],
  primary600: '#008484',
  /** @deprecated this is not a semantic color name. just use {@link mesaColorNames['Mesa - Brand/Secondary #223843'] instead} */
  secondaryDarkTeal: mesaColorNames['Mesa - Brand/Secondary #223843'],
  grey80: mesaColorNames['global/grey/80 #2F3133'],
  grey60: mesaColorNames['Greyscale/500 #6E7375'],
  grey50: mesaColorNames['semantic/fg/subtle #898D8F'],
  grey30: mesaColorNames['semantic/bg/disabled #DADDDE'],
  grey20: mesaColorNames['global/grey/20 #E6E9EB'],
} as const satisfies Record<string, ColorValue>

export type MesaColorExtensions = typeof mesaColorExtensions

const mesaSpacingExtensions = {
  '4': 16,
  base: 16,
  md: 8,
} as const satisfies Record<string, number>

export type MesaSpacingExtensions = typeof mesaSpacingExtensions

/** these are collected from the figma's text styles, under `Typography` */
export const typography = {
  XS: {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 18,
  },
  'XS Bold': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 18,
  },
  S: {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 21,
  },
  L: {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 21.6,
    letterSpacing: 0,
  },
  XL: {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 28.8,
  },
  XXL: {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 32,
    lineHeight: 38.4,
  },
  'paragraph/xs': {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 18,
  },
  'paragraph/sm': {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 21,
  },
  'paragraph/md': {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 24,
  },
  'paragraph/lg': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 27,
  },
  'label/xs-regular': {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 12,
  },
  'label/xs-bold': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 12,
  },
  'label/sm-regular': {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 14,
  },
  'label/sm-bold': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 14,
    letterSpacing: 0,
  },
  'label/md-regular': {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 16,
    letterSpacing: 0,
  },
  'label/md-bold': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 16,
  },
  'label/lg-regular': {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 18,
    lineHeight: 18,
  },
  'label/lg-bold': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 18,
  },
  'heading/heading1': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 32,
    lineHeight: 38.4,
  },
  'heading/heading2': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 28.8,
    letterSpacing: 0,
  },
  'heading/heading3': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 21.6,
  },
  'heading/heading4': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 19.2,
  },
  'heading/heading5': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 16.8,
  },
  'heading/heading6': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 14.4,
  },
  Base: {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 19.2,
  },
  'Base Bold': {
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 19.2,
  },
} as const satisfies Record<string, TextProps['style']>

const createThemeOptions = {
  lightColors: {
    primary: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
    /** @deprecated use {@link AcreTurquoise800} instead */
    secondary: '#0C5551',
    white: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    grey0: mesaColorNames['Greyscale/900 #152438'],
    grey1: mesaColorNames['Greyscale/600 #505D64'],
    grey2: mesaColorNames['Greyscale/500 #6E7375'],
    grey3: mesaColorNames['Greyscale/400 #A4AAAC'],
    grey4: mesaColorNames['Greyscale/300 #CDD4D5'],
    grey5: mesaColorNames['Greyscale/200 #EBF0F1'],
    grey6: mesaColorNames['Greyscale/100 #F5F8F8'],
    black: mesaColorNames['Mesa - Brand/Blackish #222222'],
    ...mesaColorExtensions,
  },
  colorNames: mesaColorNames,
  typography,
  spacing: {
    xl: 32,
    ...mesaSpacingExtensions,
  } as const satisfies Record<string, number>,
  classes: {
    cardShadow: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: Platform.OS === 'web' ? 1 : 2,
      },
      shadowOpacity: 0.18,
      shadowRadius: Platform.OS === 'web' ? 5 : 2.8,
      elevation: 3,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as const satisfies StyleSheet.NamedStyles<any>,
} as const

export const spacing = createThemeOptions.spacing

export const classes = createThemeOptions.classes
